import { getDeviceId } from '@zep/session-id';

const AUTH_APP_URL = process.env.NEXT_PUBLIC_AUTH_APP_URL;

/**
 * auth-app 로그인 페이지 URL을 생성합니다.
 * locale을 URL 경로에 포함하여 auth-app에서도 동일 언어로 표시되도록 합니다.
 *
 * deviceId를 query로 전달하면 auth-app이 logger state로 주입하여
 * 모든 auth-app 이벤트가 web-app의 zepDeviceId와 같은 키로 조인 가능해집니다.
 *
 * @example
 * buildAuthAppSigninUrl('en') → 'https://auth.zep.us/en/signin?service=core&deviceId=...'
 * buildAuthAppSigninUrl('ko', '/dashboard') → 'https://auth.zep.us/ko/signin?service=core&deviceId=...&callbackUrl=%2Fdashboard'
 */
export function buildAuthAppSigninUrl(
  locale: string,
  callbackUrl?: string,
  options?: {
    ssoError?: 'exchange_401';
    fromLogin?: boolean;
    ssoMethod?: string;
  },
): string {
  const authUrl = new URL(`${AUTH_APP_URL}/${locale}/signin`);
  authUrl.searchParams.set('service', 'core');

  const deviceId = getDeviceId();
  if (deviceId) {
    authUrl.searchParams.set('deviceId', deviceId);
  }

  if (callbackUrl) {
    authUrl.searchParams.set('callbackUrl', callbackUrl);
  }
  // 교환 실패로 되돌려 보내는 경우, 이동 직전에 track 하면 비콘이 나가기 전에 페이지가
  // 죽어 유실된다(dev-3 실측 0건). 이유를 쿼리로 넘겨 auth-app 이 도착 후 발화한다.
  if (options?.ssoError) {
    authUrl.searchParams.set('sso_error', options.ssoError);
    authUrl.searchParams.set('from_login', options.fromLogin ? '1' : '0');
  }
  // 자동 재로그인이 한 바퀴 돌아도 login_complete.type 이 남도록 로그인 방식을 되돌려 준다.
  if (options?.ssoMethod) {
    authUrl.searchParams.set('sso_method', options.ssoMethod);
  }
  // origin 을 넘겨야 auth-app 이 로그인 후 "호출한 그 주소"로 복귀시킨다.
  // - LOC: 로컬 개발 시 dev auth 서버가 localhost 로 redirect
  // - Vercel Preview(*.preview.zep.us): 프리뷰로 복귀 (auth-app 이 isAllowedPreviewOrigin 으로 검증)
  // 그 외(dev/stage/prod)는 origin 미전달 → 서비스 기본 URL 로 복귀
  if (typeof window !== 'undefined') {
    const isLoc = process.env.NEXT_PUBLIC_STAGE === 'LOC';
    const isPreview = /\.preview\.zep\.us$/.test(window.location.hostname);
    if (isLoc || isPreview) {
      authUrl.searchParams.set('origin', window.location.origin);
    }
  }
  return authUrl.toString();
}

// SSO 진단은 quiz-app 과 공용 단일 출처(@zep/utils/auth)를 쓴다 — 태그 키가 갈리면
// 두 앱 이벤트를 한 쿼리로 못 읽는다. 기존 import 경로 유지를 위해 재수출한다.
export {
  type SsoCookieDiagnostics,
  collectSsoCookieDiagnostics,
  getErrorHttpStatus,
  buildSsoErrorReport,
  buildSsoErrorTrackFields,
} from '@zep/utils/auth';
